import React, { useEffect, useState, useCallback, useMemo } from "react";
import AppointmentsCard from '../components/features/dashboard/AppointmentsCard';
import TransportationCard from '../components/features/dashboard/TransportationCard';
import TreatmentCard from '../components/features/dashboard/TreatmentCard';
import FinancialCard from '../components/features/dashboard/FinancialCard';
import DateSelect from '../components/common/dateSelect';
import { commonPostApi } from "../utils/apiService";
import { API_ENDPOINTS } from "../utils/apiEndpoints";
import Loader from "../components/common/loader";
import moment from "moment";
import { useSelector } from "react-redux";
import "./DashboardNew.scss";

const defaultGraphData = {
  financial: {
    series: [0, 0],
    value: [],
    totalPatients: 0,
  },
  transportation: [
    { status: "scheduled", count: 0, percentage: "0%" },
    { status: "arrived", count: 0, percentage: "0%" },
    { status: "missed", count: 0, percentage: "0%" },
  ],
  treatment: {
    categories: [],
    percentage: [],
    series: [{ data: [] }],
  },
  appointment: {
    totalAppointment: 0,
    labels: [],
    data: [],
    visitDetails: [
      {
        purpose: "New",
        nativeName: "new",
        visit_count: 0,
      },
      {
        purpose: "Follow Up",
        nativeName: "follow_up",
        visit_count: 0,
      },
    ],
  },
};

export default function DashboardNew() {
  const [show, setShow] = useState(false);
  const [date, setDate] = useState([]);
  const [selectOption, setSelectOption] = useState(1);
  const [rangeDetails, setRangeDetails] = useState("Today");
  const [loading, setLoading] = useState(false);
  const [graphData, setGraphData] = useState(defaultGraphData);

  const id = useSelector((state) => state.common.camp?.camp_details_id);

  const rangeDetail = useMemo(
    () => ({
      1: "Today",
      2: "Last 7 Days",
      3: "Last 30 Days",
      4:
        date?.[0]?.startDate && date?.[0]?.endDate
          ? `${moment(date[0].startDate).format("DD/MM/YY")}-${moment(
              date[0].endDate
            ).format("DD/MM/YY")}`
          : "",
    }),
    [date]
  );

  const fetchDashboardData = useCallback(
    async (payload = { filterType: "month" }) => {
      try {
        setLoading(true);
        const [
          patientTransportation,
          treatmentRecommended,
          appointment,
          financial,
        ] = await Promise.all([
          commonPostApi(API_ENDPOINTS.PATIENT_TRANSPORTATION_CHART, payload),
          commonPostApi(API_ENDPOINTS.TREATMENT_BREAKDOWN, payload),
          commonPostApi(API_ENDPOINTS.DASHBOARD_APPOINTMENTS, payload),
          commonPostApi(API_ENDPOINTS.PATIENT_FINANCIAL_INFORMATION, payload),
        ]);

        const totalGraph = { ...defaultGraphData };
        const appoints = appointment?.data?.data || [];
        const treatment = treatmentRecommended?.data?.data || [];

        totalGraph.transportation =
          patientTransportation?.data?.data || defaultGraphData.transportation;
        totalGraph.financial = {
          ...defaultGraphData.financial,
          series: financial?.data?.data?.[0]
            ?.slice(0, 2)
            ?.map((item) => item?.count || 0) || [0, 0],
          value:
            financial?.data?.data?.[0]
              ?.slice(0, 2)
              ?.filter((item) => item?.count) || [],
          totalPatients: financial?.data?.data?.[0]?.[2]?.count || 0,
        };

        totalGraph.appointment = {
          totalAppointment: appointment?.data?.total_count || 0,
          labels: appoints?.map((item) => item?.period || "") || [],
          data: appoints?.map((item) => item?.visit_count || 0) || [],
          visitDetails: [
            {
              purpose: "New",
              nativeName: "new",
              visit_count: appointment?.data?.new || 0,
            },
            {
              purpose: "Follow Up",
              nativeName: "follow_up",
              visit_count: appointment?.data?.follow_up || 0,
            },
          ],
        };

        const treatmentData = {
          categories: [],
          percentage: [],
          series: [
            {
              data: [],
            },
          ],
        };

        treatment.forEach(({ count = 0, percentage = 0, status = "" }) => {
          treatmentData.categories.push(status);
          treatmentData.percentage.push(Number(percentage));
          treatmentData.series[0].data.push(count);
        });

        totalGraph.treatment = treatmentData;
        setGraphData(totalGraph);
      } catch (err) {
        console.error("Error fetching dashboard data:", err);
        setGraphData(defaultGraphData);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (id) {
      setSelectOption(3);
      fetchDashboardData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    setRangeDetails(rangeDetail[selectOption] || "Today");
  }, [date, selectOption, rangeDetail]);

  const generatePayload = useCallback(
    (date1, date2, selectedOption) => {
      const filterType = {
        1: "today",
        2: "week",
        3: "month",
        4: "custom",
      };
      const payload = {};
      if (selectedOption === 4) {
        payload.startDate = date1;
        payload.endDate = date2;
      }
      payload.filterType = filterType[selectedOption];
      fetchDashboardData(payload);
    },
    [fetchDashboardData]
  );

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-header">
          <DateSelect
            show={show}
            setShow={setShow}
            onDateRangeChange={(date1, date2, selectedOption) => {
              generatePayload(date1, date2, selectedOption);
            }}
            setDashboardDate={setDate}
            selectOption={selectOption}
            setSelectOption={setSelectOption}
          />
        </div>
        <div className="dashboard-loading">
          <Loader />
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <DateSelect
          show={show}
          setShow={setShow}
          onDateRangeChange={(date1, date2, selectedOption) => {
            generatePayload(date1, date2, selectedOption);
          }}
          setDashboardDate={setDate}
          selectOption={selectOption}
          setSelectOption={setSelectOption}
        />
      </div>
      <div className="dashboard-grid">
        {/* First row */}
        <div className="dashboard-row">
          <div className="dashboard-col-60">
            <AppointmentsCard
              rangeDetails={rangeDetails}
              graphData={graphData}
            />
          </div>
          <div className="dashboard-col-40">
            <TransportationCard graphData={graphData} />
          </div>
        </div>
        {/* Second row */}
        <div className="dashboard-row">
          <div className="dashboard-col-60">
            <TreatmentCard graphData={graphData} />
          </div>
          <div className="dashboard-col-40">
            <FinancialCard graphData={graphData} />
          </div>
        </div>
      </div>
    </div>
  );
}
